import {
  Box,
  Chip,
  Menu,
  MenuItem,
  Step,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useConfirm } from "material-ui-confirm";
import moment from "moment";
import { FocusEvent, MouseEvent, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Project_Entity } from "../../entities/project";
import { AppConfig } from "../../environement";
import { mergePendingEdits } from "../../helpers/formSyncHelpers";
import {
  PROJECT_MILESTONE_FIELDS,
  projectMilestoneLabels,
  projectProgressAccent,
  projectProgressColor,
  projectProgressInk,
  projectProgressStep,
} from "../../helpers/projectProgress";
import { selectedProjectID } from "../../redux/filmsReducer";
import { useUpdateProjectMutation } from "../../redux/project/projectEndpoints";
import { useProject } from "../../redux/project/projectHooks";
import { useProjectTypes } from "../../redux/projectType/projectTypeHooks";
import { useTalents } from "../../redux/talent/talentHooks";
import { useVendors } from "../../redux/vendor/vendorHooks";
import { RouterConfig } from "../config/routerConfig";
import { ExcelContext } from "../excel";
import { useSaveError } from "../saveError/saveError";
import { Autocomplete, Button, Checkbox, Input, Sheet } from "@mui/joy";

/**
 *
 *
 * @returns {null}
 */
export default function ProjectInfo() {
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);
  const { talents } = useTalents();
  const { vendors } = useVendors();
  const { projectTypes } = useProjectTypes();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLElement) | null
  >();
  const [updateProject] = useUpdateProjectMutation();
  const [_project, setProject] = useState<Project_Entity | undefined>();
  // Fields the user has edited that the server hasn't echoed back yet.
  // `updateProject` invalidates "projects", so a GET /projects/{id} lands after
  // every save. Without this guard that response overwrites the whole form —
  // including the field currently being typed in — and the text appears to
  // delete itself.
  const [pendingFields, setPendingFields] = useState<string[]>([]);
  const confirm = useConfirm();
  const { reportSaveError, saveErrorSnackbar } = useSaveError();

  const excel = useContext(ExcelContext);

  useEffect(() => {
    if (!project) {
      setProject(undefined);
      setPendingFields([]);
      return;
    }

    // First load, or a different project: take the server state as-is.
    if (!_project || _project.id !== project.id) {
      setProject(project);
      setPendingFields([]);
      return;
    }

    // Same project: adopt the server state, but keep the edits it hasn't caught
    // up with.
    const { merged, stillPending } = mergePendingEdits<Project_Entity>(
      project,
      _project,
      pendingFields
    );

    setProject(merged);
    if (stillPending.length !== pendingFields.length)
      setPendingFields(stillPending);
    // Deliberately keyed on `project` only: this must run when server data
    // arrives, not when the local draft changes. It reads `_project` and
    // `pendingFields` from the render that scheduled it, which is the latest.
  }, [project]);

  if (!_project) return <div />;

  const handleArchive = () => {
    confirm({
      description: project!.archived
        ? `This project will move out of the Archived tab.`
        : `This project will move to the Archived tab. You keep full access to it and can bring it back at any time.`,
    })
      .then(() => {
        updateProject({
          id: project!.id,
          body: { archived: !project!.archived },
        });
      })
      .catch(() => {
        /* ... */
      });
  };

  const handleDelete = () => {
    confirm({
      description: `IMPORTANT MESSAGE: YOU ARE ABOUT TO DELETE A PROJECT. YOU WON'T BE ABLE TO RECOVER ANYTHING FROM THIS PROJECT. PLEASE CONFIRM YOUR ACTION`,
    })
      .then(() => {
        updateProject({ id: project!.id, body: { deleted: true } });
        navigate(`/${RouterConfig.projects}`);
      })
      .catch(() => {
        /* ... */
      });
  };

  const handleTextChange = (name: string) => (value: string | number) => {
    setPendingFields((fields) =>
      fields.includes(name) ? fields : [...fields, name]
    );
    setProject((p) => (p ? ({ ...p, [name]: value } as Project_Entity) : p));
  };

  const handleFieldBlur =
    (field: keyof Project_Entity) => (e: FocusEvent<HTMLInputElement>) => {
      const value = e.target.value;
      handleTextChange(field)(value);

      // Unchanged: don't save. A pointless PUT would invalidate "projects" and
      // trigger another refetch of this form for nothing.
      if ((project?.[field] ?? "") === value) {
        setPendingFields((fields) => fields.filter((f) => f !== field));
        return;
      }

      // Send only this field. Sending the whole entity (as this used to) also
      // pushed back a possibly stale copy of the server-computed totals.
      save({ [field]: value });
    };

  const savePartial = (body: Partial<Project_Entity>) => {
    setProject((p) => (p ? { ...p, ...body } : p));
    save(body);
  };

  function save(body: Partial<Project_Entity>) {
    if (!project) return;

    updateProject({ id: project.id, body })
      .unwrap()
      .catch((error) => {
        // Release the field so the next server response can correct it,
        // instead of leaving the form stuck showing a value that never saved.
        setPendingFields((fields) =>
          fields.filter((f) => !Object.keys(body).includes(f))
        );
        reportSaveError(error);
      });
  }

  const steps = ["Active", "Invoiced", "Paid"];

  if (!project) return <Box />;

  const milestones = project.milestones();
  const milestonesReached = project.milestonesReached();
  const progressStep = projectProgressStep(
    milestonesReached,
    milestones.length
  );

  // Built from the same list the projects list charts, so the two can't drift
  // apart on wording or order. A "paid" step only opens once its "sent" step
  // has happened, which is every second one.
  const chips = projectMilestoneLabels(project.union).map((label, i) => ({
    label,
    field: PROJECT_MILESTONE_FIELDS[i],
    active: milestones[i],
    disabled: i % 2 === 1 && !milestones[i - 1],
  }));

  // Ticked-off chips wear the step's own colour rather than a fixed green, so
  // they read as part of the panel they sit on instead of fighting it.
  const accent = projectProgressAccent(progressStep);
  const ink = projectProgressInk(progressStep);
  const activeChipSx = {
    backgroundColor: accent,
    color: ink,
    "&:hover": { backgroundColor: accent },
    "& .MuiChip-deleteIcon": {
      color: ink,
      opacity: 0.7,
      "&:hover": { color: ink, opacity: 1 },
    },
  };

  const sortedTalents = [...(talents ?? [])].sort((a, b) => {
    const lastNameCompare = (a.lastName ?? "").localeCompare(b.lastName ?? "");
    if (lastNameCompare !== 0) return lastNameCompare;
    return (a.firstName ?? "").localeCompare(b.firstName ?? "");
  });
  const sortedProjectTypes = [...(projectTypes ?? [])].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "")
  );
  const sortedVendors = [...(vendors ?? [])].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "")
  );

  const selectedPrincipal =
    sortedTalents.find((talent) => talent.id === (_project.principalID ?? 0)) ??
    null;
  const selectedProjectType =
    sortedProjectTypes.find(
      (projectType) => projectType.id === (_project.projectTypeID ?? 0)
    ) ?? null;
  const selectedPayroll =
    sortedVendors.find((vendor) => vendor.id === (_project.payrollID ?? 0)) ??
    null;
  const selectedUnion =
    sortedVendors.find((vendor) => vendor.id === (_project.unionID ?? 0)) ??
    null;
  const selectedSignatory =
    sortedVendors.find((vendor) => vendor.id === (_project.signatoryID ?? 0)) ??
    null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "stretch",
        flex: 1,
      }}
    >
      <Box
        sx={{
          background: "#D3663E",
          flex: 1,
          color: "white",
          p: 1,
          display: "flex",
          justifyContent: "center",
          gap: 1,
        }}
      >
        <ActionButton
          faIcon="fa-duotone fa-file-contract"
          // onClick={excel.contract}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          title={project.union ? "AFM Contract" : "Client Invoice"}
        />
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem
            onClick={() => {
              excel.contract();
              setAnchorEl(null);
            }}
          >
            <i className="fa-duotone fa-file-excel pright"></i> Excel
          </MenuItem>
          <MenuItem
            onClick={() => {
              window.open(
                `${AppConfig.api.endpoint}/films/projects/${project.id}/contract`,
                "_blank"
              );
              setAnchorEl(null);
            }}
          >
            <i className="fa-duotone fa-file-pdf pright"></i>PDF
          </MenuItem>
        </Menu>
        <ActionButton
          faIcon="fa-duotone fa-file-spreadsheet"
          onClick={excel.musicPrepSummary}
          title={"Music Prep Summary"}
        />
        <ActionButton
          faIcon="fa-duotone fa-file-invoice"
          onClick={() => {
            window.open(
              `${AppConfig.api.endpoint}/films/projects/${project.id}/invoices`,
              "_blank"
            );
          }}
          title={project.union ? "Copyists Invoices" : "Vendor Invoices"}
        />
        <ActionButton
          faIcon="fa-duotone fa-file-invoice"
          onClick={() => {
            window.open(
              `${AppConfig.api.endpoint}/films/projects/${project.id}/orchInvoices`,
              "_blank"
            );
          }}
          title={"Orchestrators Invoices"}
        />
        <ActionButton
          faIcon="fa-duotone fa-receipt"
          onClick={() => {
            window.open(
              `${AppConfig.api.endpoint}/films/projects/${project.id}/expensesInvoices`,
              "_blank"
            );
          }}
          title={"Expenses Invoice"}
        />
      </Box>
      <Box
        sx={{
          maxWidth: 600,
          width: "100%",
          marginLeft: "auto",
          marginRight: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          p: 2,
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Stepper
            nonLinear
            activeStep={steps.indexOf(project.status)}
            alternativeLabel
          >
            {steps.map((label, i) => (
              <Step key={label} color="success">
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        {/* Same green scale as the projects list, so the colour a project
            picks up here is the colour you then look for in the list. */}
        <Sheet
          variant="outlined"
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
            backgroundColor: projectProgressColor(progressStep),
            transition: "background-color .3s",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            {chips.map((c) => (
              <Tooltip
                title={
                  c.active
                    ? `${moment(project[c.field]).format(
                        "lll"
                      )}. Click on the crossmark to undo.`
                    : ""
                }
                key={c.label}
              >
                <Chip
                  size="small"
                  variant={c.active ? "filled" : "outlined"}
                  disabled={c.disabled}
                  sx={c.active ? activeChipSx : undefined}
                  onDelete={
                    c.active
                      ? () =>
                          updateProject({
                            id: _project.id,
                            body: { [c.field]: project.reversedDate() },
                          })
                      : undefined
                  }
                  label={c.label}
                  onClick={() =>
                    updateProject({
                      id: _project.id,
                      body: { [c.field]: moment().utc().format() },
                    })
                  }
                />
              </Tooltip>
            ))}
          </Box>
          <Typography variant="caption">
            {milestonesReached} of {milestones.length} steps complete
            {project.completed() && !project.archived
              ? " — this project is under Completed"
              : ""}
          </Typography>
        </Sheet>
        <Sheet
          variant="outlined"
          sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1 }}
        >
          <Input
            disabled={project?.locked()}
            value={_project.name ?? ""}
            startDecorator="Name:"
            onBlur={handleFieldBlur("name")}
            onChange={(e) => handleTextChange("name")(e.target.value)}
          />
          <Input
            disabled={project?.locked()}
            value={_project.referenceNumber ?? ""}
            startDecorator="Reference Number:"
            onBlur={handleFieldBlur("referenceNumber")}
            onChange={(e) =>
              handleTextChange("referenceNumber")(e.target.value)
            }
          />
          <Input
            disabled={project?.locked()}
            value={_project.invoiceNumber ?? ""}
            startDecorator="Invoice Number:"
            onBlur={handleFieldBlur("invoiceNumber")}
            onChange={(e) => handleTextChange("invoiceNumber")(e.target.value)}
          />
          <Autocomplete
            disabled={project?.locked()}
            value={selectedProjectType}
            placeholder="Type"
            startDecorator="Type:"
            options={sortedProjectTypes}
            getOptionLabel={(option) => option?.name ?? ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) =>
              savePartial({ projectTypeID: value?.id ?? 0 })
            }
            slotProps={{
              listbox: { sx: { zIndex: 9999 } },
            }}
          />
          <Checkbox
            disabled={project?.locked()}
            checked={!!project?.newMedia}
            label="New Media"
            onChange={(e) => savePartial({ newMedia: e.target.checked })}
          />
          <Input
            type="date"
            disabled={project?.locked()}
            value={
              _project.date ? moment(_project.date).format("YYYY-MM-DD") : ""
            }
            startDecorator="Date:"
            onChange={(e) =>
              savePartial({
                date: e.target.value
                  ? moment(e.target.value).utc().format()
                  : "",
              })
            }
          />
          <Autocomplete
            disabled={project?.locked()}
            value={selectedPrincipal}
            placeholder="Project Principal"
            startDecorator="Project Principal:"
            options={sortedTalents}
            getOptionLabel={(option) =>
              option ? `${option.firstName} ${option.lastName}` : ""
            }
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) =>
              savePartial({ principalID: value?.id ?? 0 })
            }
            slotProps={{
              listbox: { sx: { zIndex: 9999 } },
            }}
          />
          <Input
            disabled={project?.locked()}
            value={_project.leader ?? ""}
            startDecorator="Leader:"
            onBlur={handleFieldBlur("leader")}
            onChange={(e) => handleTextChange("leader")(e.target.value)}
          />
          <Autocomplete
            disabled={project?.locked()}
            value={selectedPayroll}
            placeholder="Payroll"
            startDecorator="Payroll:"
            options={sortedVendors}
            getOptionLabel={(option) => option?.name ?? ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => savePartial({ payrollID: value?.id ?? 0 })}
            slotProps={{
              listbox: { sx: { zIndex: 9999 } },
            }}
          />
          <Checkbox
            disabled={project?.locked()}
            checked={!!project?.union}
            label="Union Project"
            onChange={(e) => savePartial({ union: e.target.checked })}
          />
          <Autocomplete
            disabled={!_project.union || project?.locked()}
            value={selectedUnion}
            placeholder="Union"
            startDecorator="Union:"
            options={sortedVendors}
            getOptionLabel={(option) => option?.name ?? ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) => savePartial({ unionID: value?.id ?? 0 })}
            slotProps={{
              listbox: { sx: { zIndex: 9999 } },
            }}
          />
          <Input
            disabled={project?.locked()}
            value={_project.local ?? ""}
            startDecorator="Local:"
            onBlur={handleFieldBlur("local")}
            onChange={(e) => handleTextChange("local")(e.target.value)}
          />
          <Autocomplete
            disabled={project?.locked()}
            value={selectedSignatory}
            placeholder="Signatory"
            startDecorator="Signatory:"
            options={sortedVendors}
            getOptionLabel={(option) => option?.name ?? ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={(_, value) =>
              savePartial({ signatoryID: value?.id ?? 0 })
            }
            slotProps={{
              listbox: { sx: { zIndex: 9999 } },
            }}
          />
          <Input
            disabled={project?.locked()}
            value={_project.placeOfEngagement ?? ""}
            startDecorator="Place of Engagement:"
            onBlur={handleFieldBlur("placeOfEngagement")}
            onChange={(e) =>
              handleTextChange("placeOfEngagement")(e.target.value)
            }
          />
          <Input
            disabled={project?.locked()}
            value={_project.memo ?? ""}
            startDecorator="Memo:"
            onBlur={handleFieldBlur("memo")}
            onChange={(e) => handleTextChange("memo")(e.target.value)}
          />
          <Input
            disabled={project?.locked()}
            value={_project.hwCompany ?? ""}
            startDecorator="Health & Welfare Company:"
            onBlur={handleFieldBlur("hwCompany")}
            onChange={(e) => handleTextChange("hwCompany")(e.target.value)}
          />
        </Sheet>
        <Sheet variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" color="error">
            Danger Zone
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {/* Deliberately not gated on locked(): a project locks as soon as
                it leaves "Active", which is exactly when it becomes worth
                filing away. Gating it here made completed projects
                unarchivable. */}
            <Button variant="outlined" color="error" onClick={handleArchive}>
              {project?.archived ? "Unarchive Project" : "Archive Project"}
            </Button>
            <Tooltip
              title={
                project?.archived
                  ? "Delete for ever"
                  : "Only archived projects can be deleted"
              }
            >
              <Box>
                <Button
                  disabled={!project?.archived}
                  variant="contained"
                  color="error"
                  onClick={handleDelete}
                >
                  Delete Project
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </Sheet>
      </Box>
      {saveErrorSnackbar}
    </Box>
  );
}

function ActionButton({
  faIcon,
  title,
  onClick,
}: {
  faIcon?: string;
  title?: string;
  onClick: (e: MouseEvent<HTMLElement>) => void;
}) {
  return (
    <Box
      sx={{
        width: 150,
        background: "rgba(255,255,255,0.2)",
        border: "solid 1px rgba(255,255,255,0.3)",
        borderRadius: 2,
        textAlign: "center",
        cursor: "pointer",
        p: 1,
        transition: "background .2s",
        "&:hover": {
          background: "rgba(255,255,255,0.5)",
        },
      }}
      onClick={(e) => onClick(e)}
    >
      <Box sx={{ fontSize: 32 }}>
        <i className={faIcon}></i>
      </Box>
      <Typography variant="caption">{title}</Typography>
    </Box>
  );
}
